import type { ResultItem, Sort } from '../../@types';
import { ResultSource } from './SearchResultSource';

export class SearchResults {
	resultSources: Array<ResultSource>;
	sort: Sort;
	totalDuration: number;
	totalResults: number;
	uniqueId: number;

	constructor(resultSources: Array<ResultSource>, sort: Sort, duration: number) {
		this.resultSources = resultSources;
		this.sort = sort;
		this.totalDuration = duration;
		this.uniqueId = new Date().getTime();
		this.totalResults = resultSources.reduce((acc, curr) => acc + (curr.results.totalResults || 0), 0); // If a result soure has an error body, it won't have `totalResults`
	}

	getResultSources(): Array<ResultSource> {
		return this.resultSources;
	}

	/**
	 * Returns the total number of search results available
	 *
	 * @returns the number of results that were returned
	 */
	getTotalResults(): number {
		return this.totalResults;
	}

	getTotalDuration(): number {
		return this.totalDuration;
	}

	getDurations(): Array<Record<string, any>> {
		// I think eslint is confused
		// eslint-disable-next-line arrow-body-style
		const durations: Array<Record<string, any>> = this.resultSources.map((resultSource) => {
			return {
				searchableName: resultSource.getSearchableName(),
				duration: resultSource.getDuration(),
			};
		});
		return durations;
	}

	getNextResult(): Promise<ResultItem | undefined> {
		// if there are no more items, this will return undefined
		return this._getResult();
	}

	/**
	 *
	 * @param {number} n the number of resultItems to get
	 *
	 * @returns Array<ResultItems> - the array will be empty if there are no results left
	 */
	async getNResults(n: number): Promise<Array<ResultItem>> {
		const results: ResultItem[] = [];

		for (let i = 0; i < n; ++i) {
			const result = await this._getResult();

			// only add the result if it is valid
			if (result) {
				results.push(result);
			}
		}

		return results;
	}

	async _getResult(): Promise<ResultItem | undefined> {
		// get the offers from the result sources
		const offers: Array<ResultItem | undefined> = [];

		for (const item of this.resultSources) {
			const resultItem = await item.offerResultItem();
			offers.push(resultItem);
		}

		// pick a result from the offers based on the sort order
		const result = offers.reduce((acc, curr) => {
			let accumulator = acc;

			if (curr !== undefined) {
				if (acc === undefined) {
					// we have a curr resultItem, but not an acc
					accumulator = curr;
				} else {
					// work the magic
					switch (this.sort.sortBy) {
						case 'Name':
							{
								const accName = acc.attributes.name;
								const currName = curr.attributes.name;

								if (this.sort.sortOrder === 'Ascending') {
									accumulator =
										currName.localeCompare(accName, undefined, {
											numeric: true,
										}) < 0
											? curr
											: acc;
								} else {
									accumulator =
										currName.localeCompare(accName, undefined, {
											numeric: true,
										}) > 0
											? curr
											: acc;
								}
							}
							break;

						case 'ModificationDate':
							{
								const accDate = acc.attributes.modificationDate;
								const currDate = curr.attributes.modificationDate;

								if (this.sort.sortOrder === 'Ascending') {
									accumulator = currDate < accDate ? curr : acc;
								} else {
									accumulator = currDate > accDate ? curr : acc;
								}
							}
							break;

						case 'Size':
							{
								const accSize = acc.attributes.fileSize;
								const currSize = curr.attributes.fileSize;

								if (this.sort.sortOrder === 'Ascending') {
									accumulator = currSize < accSize ? curr : acc;
								} else {
									accumulator = currSize > accSize ? curr : acc;
								}
							}
							break;
					}
				}
			}

			// return updated accumulator
			return accumulator;
		}, undefined);

		if (result) {
			// find the resultItem index in the results array
			const i = offers.indexOf(result);
			const resultSource = this.resultSources[i];
			// let the result source know that its offer was accepted
			resultSource.offerAccepted();
		}

		return result;
	}
}
